import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createPaymentInvoice, getTransactionDetails } from '../../services/payment/payment-service';
import { getDNAPaymentScript, getPublicConfigurationValue } from '../../utils/payment';
import './style.css';
import images from 'assets/images';
import { env } from '../../config/env';
import { Common } from '../../utils/constants';
import type { IDNAPaymentsType, IPaymentInvoiceResponse, ITransactionDetails } from '../../models/payment';
import type { IPaymentFailProps } from '../../types/propTypes';

declare global {
    interface Window {
        DNAPayments: IDNAPaymentsType;
    }
}

const PaymentFail = (props: IPaymentFailProps) => {
    const { t } = props;
    const { code, invoice_id, connector_id } = useParams();
    const navigate = useNavigate();
    const [transactionDetails, setTransactionDetails] = useState<ITransactionDetails | null>(null);

    useEffect(() => {
        const handleTransactionDetails = async () => {
            await getTransactionDetails(invoice_id)
                .then((res) => {
                    if (res.Data && Object.keys(res.Data).length !== 0) {
                        setTransactionDetails(res.Data);
                    }
                })
                .catch((m) => {
                    if (m.response && m.response.data && m.response.data.message) {
                        toast.error(m.response.data.message);
                    } else {
                        toast.error(m.toString());
                    }
                });
        };

        handleTransactionDetails();
    }, []);

    const backbtnclick = () => {
        navigate('/');
    };

    const handleRetry = async () => {
        const enable_google_apple_pay = await getPublicConfigurationValue(
            Common.ConfigurationKey.ENABLE_GOOGLE_PAY_APPLE_PAY_DNA,
        );
        const email = localStorage.getItem('email') || '';

        if (connector_id && email) {
            const paymentInvoice = await generatePaymentInvoice({
                connector_id,
                email,
            });
            if (paymentInvoice) {
                document.getElementById('loading').classList.remove('spinner-disable');
                const isTestMode = !!(
                    paymentInvoice.isTestMode === undefined ||
                    paymentInvoice.isTestMode === null ||
                    paymentInvoice.isTestMode === true
                );
                const script = document.createElement('script');
                script.type = 'text/javascript';
                script.src = getDNAPaymentScript(isTestMode);
                script.nonce = 'ils+vsV+OAN1z2QUGBzoyQ==';
                script.async = true;
                script.onload = () => {
                    window.DNAPayments.configure({
                        autoRedirectDelayInMs: 3000,
                        scopes: {
                            allowHosted: true,
                        },
                        paymentMethods: [
                            {
                                name: window.DNAPayments.paymentMethods.BankCard,
                            },
                            ...(enable_google_apple_pay === 'true'
                                ? [
                                      {
                                          name: window.DNAPayments.paymentMethods.GooglePay,
                                      },
                                      {
                                          name: window.DNAPayments.paymentMethods.ApplePay,
                                      },
                                  ]
                                : []),
                        ],
                        isTestMode: isTestMode,
                        paymentTimeoutInSeconds: 900,
                    });

                    const invoiceId = paymentInvoice.invoice_id;

                    const paymentData = {
                        paymentSettings: {
                            terminalId: paymentInvoice.terminal,
                            returnUrl: `${window.location.origin}/portal/charge-station/${code}/${connector_id}/payment/success/${invoiceId}`,
                            failureReturnUrl: `${window.location.origin}/portal/charge-station/${code}/${connector_id}/payment/fail/${invoiceId}`,
                            callbackUrl: `${env.API_SERVER}/${env.API_VERSION}/web-pay/payment/dna-webhook/${invoiceId}`,
                            failureCallbackUrl: `${env.API_SERVER}/${env.API_VERSION}/web-pay/payment/dna-webhook/${invoiceId}`,
                            cscMatrix: 22,
                            paMatrix: 3,
                        },
                        customerDetails: {
                            accountDetails: {
                                accountId: invoiceId,
                            },
                        },
                        periodic: {
                            periodicType: 'ucof',
                        },
                        invoiceId: invoiceId,
                        amount: +paymentInvoice.amount,
                        auth: { access_token: paymentInvoice.access_token },
                        language: 'en',
                        description: 'Pre auth to InstaVolt',
                        transactionType: paymentInvoice.transaction_type || 'PRE-AUTH',
                        currency: paymentInvoice.currency,
                    };

                    window.DNAPayments.openPaymentPage(paymentData);
                };
                script.onerror = () => {
                    document.getElementById('loading').classList.add('spinner-disable');
                    toast.error(t<string>('paymentFail.common.error'));
                };
                document.body.appendChild(script);
            }
        } else {
            toast.error(t<string>('paymentFail.common.error'));
            navigate('/');
        }
    };

    const generatePaymentInvoice = async (inputData) => {
        let paymentInvoice: IPaymentInvoiceResponse;
        await createPaymentInvoice(+inputData.connector_id, inputData.email)
            .then(async (res) => {
                if (res.Data) {
                    paymentInvoice = res.Data;
                }
            })
            .catch((m) => {
                if (m.response && m.response.data && m.response.data.message) {
                    toast.error(m.response.data.message);
                } else {
                    toast.error(m.toString());
                }
            });
        return paymentInvoice;
    };

    return (
        <>
            <Helmet>
                <title>{t<string>('paymentFail.title')}</title>
            </Helmet>
            <div className="card-body text-center payment-fail">
                <div className="row min-vh-50 align-items-center">
                    <div className="col">
                        <div className="mesg-box text-center">
                            <img src={images.errorIconSVG} alt="error" />
                            <h2 className="error-color">{t<string>('paymentFail.title')}</h2>
                            <p>{t<string>('paymentFail.failureLabel')}</p>
                        </div>
                        {transactionDetails && transactionDetails.error_message ? (
                            <div className="mt-3 error-color">
                                <p className="error-message">{transactionDetails.error_message}</p>
                            </div>
                        ) : (
                            <></>
                        )}

                        <div className="clearfix mt-4">
                            <button type="button" className="btn btn-primary mr-2" onClick={() => handleRetry()}>
                                <span>{t<string>('paymentFail.button.retry')}</span>
                            </button>
                            <button type="button" className="btn btn-primary" onClick={() => backbtnclick()}>
                                <span>{t<string>('paymentFail.button.back')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
export default PaymentFail;
